import React, { useCallback, useEffect, useState } from "react";
import { Form, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import Loading from "../../../components/LoadingPage/Loading";
import ExistingBarcodesModal, { BarcodeItem } from "./ExistingBarcodesModal";
import ItemListingTitle from "../../../components/itemListingTitle/itemListingTitle";
import {
  useGetMckessonEdiFileListMutation,
  useGetMckessonEdiItemListMutation,
  useMarkMckessonOrderProcessedMutation,
} from "../../../redux/mckesson/mckessonApiSlice";
import { useItemSearchMutation, useGetItemCreateDropdownValMutation } from "../../../redux/inventory/inventoryApiSlice";
import { getErrorMessage } from "../../../utils/commonUtils";
import "./McOrderProcessor.scss";

interface OrderFile {
  id: string;
  fileName: string;
  fileContent: string;
  timestamp: string;
  loaded: boolean;
  processed: boolean;
  isActive: boolean;
  createdOnUtc: string;
  updatedOnUtc: string;
  createdBy: string;
  updatedBy: string;
}

interface OrderDataItem {
  id: string;
  fileId: string;
  hlId: number;
  vc: string;
  mg: string;
  lot: string | null;
  ndc: string | null;
  qtyOrdered: number;
  qtyshipped: number;
  uom: string;
  qtyShippedInvUom: number;
  qtyReceived: number;
  invUom: string;
  itemDescription: string;
  expDate: string | null;
  processed: boolean;
  reason: string | null;
  isActive: boolean;
  createdOnUtc: string;
  updatedOnUtc: string | null;
  createdBy: string;
  updatedBy: string | null;
}

interface OrderInfo {
  orderNumber: string;
  orderDate: string;
  trackingNumber: string;
  fileName: string;
  orderingLocationCode: string;
  orderingLocationName: string;
}

const parseEdiOrderInfo = (fileContent: string, fileName: string): OrderInfo => {
  const segments = fileContent.split("~").map((s) => s.trim());
  let orderNumber = "";
  let orderDate = "";
  let trackingNumber = "";
  let orderingLocationCode = "";
  let orderingLocationName = "";

  for (const segment of segments) {
    const elements = segment.split("*");
    const segId = elements[0];

    if (segId === "PRF" && elements.length > 1) {
      orderNumber = elements[1] || "";
      if (elements.length > 4 && elements[4]) {
        const d = elements[4];
        orderDate = `${parseInt(d.substring(4, 6))}/${parseInt(d.substring(6, 8))}/${d.substring(0, 4)}`;
      }
    } else if (segId === "MAN" && elements.length > 2) {
      trackingNumber = elements[2] || "";
    } else if (segId === "N1" && elements.length > 2 && elements[1] === "ST") {
      orderingLocationName = elements[2] || "";
      if (elements.length > 4) {
        orderingLocationCode = elements[4] || "";
      }
    }
  }

  return { orderNumber, orderDate, trackingNumber, fileName, orderingLocationCode, orderingLocationName };
};

const defaultFilter = { searchText: "", active: true };

const McOrderProcessor: React.FC = () => {
  const [searchVal, setSearchVal] = useState({ ...defaultFilter });
  const [selectedStock, setSelectedStock] = useState("");
  const [orderFiles, setOrderFiles] = useState<OrderFile[]>([]);
  const [dataItems, setDataItems] = useState<OrderDataItem[]>([]);
  const [orderInfo, setOrderInfo] = useState<OrderInfo | null>(null);
  const [selectedFileId, setSelectedFileId] = useState<string>("");
  const [showBarcodePopup, setShowBarcodePopup] = useState(false);
  const [barcodeItems, setBarcodeItems] = useState<BarcodeItem[]>([]);
  const [stocks, setStocks] = useState<{ id: string; stockCode: string; stockName: string }[]>([]);
  const [stockError, setStockError] = useState("");

  const [getEdiFileList, { isLoading: filesLoading }] =
    useGetMckessonEdiFileListMutation();
  const [getEdiItemList, { isLoading: dataItemsLoading }] =
    useGetMckessonEdiItemListMutation();
  const [itemSearch, { isLoading: fetchLoading }] =
    useItemSearchMutation();
  const [markProcessed, { isLoading: markProcessedLoading }] =
    useMarkMckessonOrderProcessedMutation();
  const [getDropdownVal] = useGetItemCreateDropdownValMutation();

  const isLoading =
    filesLoading || dataItemsLoading || fetchLoading || markProcessedLoading;

  const loadFiles = useCallback(async () => {
    try {
      const res = await getEdiFileList({
        searchText: searchVal.searchText,
        active: searchVal.active,
      }).unwrap();
      if (res?.result) {
        setOrderFiles(res.result);
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  }, [getEdiFileList, searchVal]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  useEffect(() => {
    const loadStocks = async () => {
      try {
        const res = await getDropdownVal({}).unwrap();
        if (res?.result?.stocks) {
          setStocks(res.result.stocks);
        }
      } catch (error) {
        toast.error(getErrorMessage(error));
      }
    };
    loadStocks();
  }, [getDropdownVal]);

  const handleFileClick = async (file: OrderFile) => {
    setSelectedFileId(file.id);
    try {
      const [itemRes, fileRes] = await Promise.all([
        getEdiItemList({ fileId: file.id }).unwrap(),
        getEdiFileList({ fileId: file.id }).unwrap(),
      ]);
      if (itemRes?.result) {
        setDataItems(itemRes.result ?? []);
      }
      if (fileRes?.result?.length > 0) {
        const fileData = fileRes.result[0];
        setOrderInfo(parseEdiOrderInfo(fileData.fileContent, fileData.fileName));
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleFetch = async (item: OrderDataItem) => {
    if (!selectedStock) {
      setStockError("Stock is required");
      return;
    }
    setStockError("");
    try {
      const res = await itemSearch({
        searchText: "",
        active: true,
        includeDeleted: false,
        ndcCode: item.ndc || "",
        lotNo: item.lot || "",
        expDate: item.expDate || "",
        productNo: "",
        sourceRcv: selectedStock,
        sortColumn: "",
        sortDirection: "",
      }).unwrap();
      if (res?.result?.length > 0) {
        const mappedItems: BarcodeItem[] = res.result.map((r: any) => ({
          id: r.id,
          barcode: r.rxSRXId,
          ndc: r.ndcCode,
          lot: r.lotNo,
          expDate: r.expiryDate,
          source: r.stock?.stockCode || "",
          orderingProductNo: r.orderingProductMaster?.productNo || "",
        }));
        setBarcodeItems(mappedItems);
        setShowBarcodePopup(true);
      } else {
        setBarcodeItems([]);
        setShowBarcodePopup(true);
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleBarcodeSave = (selectedBarcode: any, qty: string, refInfo: string) => {
    setShowBarcodePopup(false);
    toast.success("Barcode saved successfully");
    handleFileClick({ id: selectedFileId } as OrderFile);
  };

  const handleBarcodeCancel = () => {
    setShowBarcodePopup(false);
  };

  const handleNewBarcodeSave = (values: any) => {
    toast.success("Barcode created successfully");
  };

  const handleMarkProcessed = async (item: OrderDataItem) => {
    try {
      const res = await markProcessed({
        fileId: selectedFileId,
        itemId: item.id,
      }).unwrap();
      if (res?.success) {
        toast.success(res.message || "Item marked as processed");
        handleFileClick({ id: selectedFileId } as OrderFile);
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  return (
    <div className="mc-order-processor-wrapper">
      {isLoading && <Loading />}

      <ItemListingTitle
        title="McKesson Order Processor"
        disRecords={orderFiles.length}
        totalRecords={orderFiles.length}
        searchVal={searchVal}
        setSearchVal={setSearchVal}
        defaultFilter={defaultFilter}
        isAddPermitted={false}
        checkboxLabel="Pending"
        clearButtonText="Clear All / Refresh"
      />

      {/* Files Table */}
      <div className="listing-table-wrapper">
        <Table responsive="md" hover={true}>
          <thead>
            <tr>
              <th>File Name</th>
              <th>Timestamp</th>
              <th>Processed?</th>
              <th>Created By</th>
              <th>Edited By</th>
            </tr>
          </thead>
          <tbody>
            {orderFiles.length > 0 ? (
              orderFiles.map((file) => (
                <tr
                  key={file.id}
                  className={selectedFileId === file.id ? "selected" : ""}
                  onClick={() => handleFileClick(file)}
                >
                  <td>
                    <span className="file-link">{file.fileName}</span>
                  </td>
                  <td>{file.timestamp}</td>
                  <td>{file.processed ? "Yes" : "No"}</td>
                  <td>{file.createdBy}</td>
                  <td>{file.updatedBy}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} className="text-center">
                  No records found
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Data Items Section */}
      {orderInfo && (
        <div className="data-items-section">
          <h5 className="section-title">Data Items</h5>

          {/* Order Info */}
          <div className="order-info-grid">
            <div className="order-info-row">
              <div className="info-item">
                <label>Order # (SRX):</label>
                <span>{orderInfo.orderNumber}</span>
              </div>
              <div className="info-item">
                <label>Order Date:</label>
                <span>{orderInfo.orderDate}</span>
              </div>
            </div>
            <div className="order-info-row">
              <div className="info-item">
                <label>Tracking #:</label>
                <span>{orderInfo.trackingNumber}</span>
              </div>
              <div className="info-item">
                <label>File Name:</label>
                <span>{orderInfo.fileName}</span>
              </div>
            </div>
            <div className="order-info-row">
              <div className="info-item">
                <label>Ordering Location Code:</label>
                <span>{orderInfo.orderingLocationCode}</span>
              </div>
              <div className="info-item">
                <label>Ordering Location Name:</label>
                <span>{orderInfo.orderingLocationName}</span>
              </div>
            </div>
            <div className="order-info-row">
              <div className="info-item">
                <label>Stock:</label>
                <div>
                  <Form.Select
                    size="sm"
                    value={selectedStock}
                    onChange={(e) => {
                      setSelectedStock(e.target.value);
                      if (e.target.value) setStockError("");
                    }}
                    className="stock-select"
                    isInvalid={!!stockError}
                  >
                    <option value="">Select</option>
                    {stocks.map((stock) => (
                      <option key={stock.id} value={stock.stockCode}>
                        {stock.stockName}
                      </option>
                    ))}
                  </Form.Select>
                  {stockError && (
                    <div className="text-danger" style={{ fontSize: "0.875rem", marginTop: "0.25rem" }}>
                      {stockError}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Data Items Table */}
          <div className="listing-table-wrapper">
            <Table responsive="md" hover={true}>
              <thead>
                <tr>
                  <th>HL Id</th>
                  <th>VC</th>
                  <th>MG</th>
                  <th>Lot</th>
                  <th>NDC</th>
                  <th>Qty Ordered</th>
                  <th>Qty Shipped</th>
                  <th>UOM</th>
                  <th>Qty Shipped (Inv. Uom)</th>
                  <th>Qty Received</th>
                  <th>Inv. Uom</th>
                  <th>ItemDescription</th>
                  <th>ExpDate</th>
                  <th>Processed?</th>
                  <th>Reason</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {dataItems.length > 0 ? (
                  dataItems.map((item, index) => (
                    <tr key={item.id || index}>
                      <td>{item.hlId}</td>
                      <td>{item.vc}</td>
                      <td>{item.mg}</td>
                      <td>{item.lot}</td>
                      <td>{item.ndc}</td>
                      <td>{item.qtyOrdered?.toFixed(2)}</td>
                      <td>{item.qtyshipped?.toFixed(2)}</td>
                      <td>{item.uom}</td>
                      <td>{item.qtyShippedInvUom?.toFixed(2)}</td>
                      <td>{item.qtyReceived?.toFixed(2)}</td>
                      <td>{item.invUom}</td>
                      <td>{item.itemDescription}</td>
                      <td>{item.expDate}</td>
                      <td>{item.processed ? "Yes" : "No"}</td>
                      <td>{item.reason}</td>
                      <td className="action-cell">
                        <span
                          className="action-link"
                          onClick={() => handleFetch(item)}
                        >
                          Fetch
                        </span>
                        <span
                          className="action-link"
                          onClick={() => handleMarkProcessed(item)}
                        >
                          Mark Processed
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={16} className="text-center">
                      No data items found
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </div>
      )}
      {/* Existing Barcodes Popup */}
      {showBarcodePopup && (
        <ExistingBarcodesModal
          barcodeItems={barcodeItems}
          onSave={handleBarcodeSave}
          onClose={handleBarcodeCancel}
          onNewBarcodeSave={handleNewBarcodeSave}
        />
      )}
    </div>
  );
};

export default McOrderProcessor;
